import type { QueryClient } from "@tanstack/react-query";

import { useAgentStore } from "../agent/agentStore";
import { useDraftStore } from "../agent/draftStore";
import { pointToBus } from "../agent/pointTo";
import { useAuthNotice } from "../api/authNotice";
import { queryKey, runQuery } from "../api/endpoints";
import type { MeView } from "../api/types";
import { pointToPath } from "../app/screenRoutes";
import { showToast } from "../ui/toastStore";
import type { LiveSink } from "./liveClient";
import { getHeartbeatLocation } from "./location";
import { toastForNotice } from "./notices";
import { useLiveStore } from "./store";

/** Queries that belong to the page and the person, not to the active discipline. */
export const GLOBAL_QUERIES: ReadonlySet<unknown> = new Set(["me", "health"]);

/** Throw away what was read in another discipline (the person switched). */
export function dropWorkspaceQueries(queryClient: QueryClient): void {
  queryClient.removeQueries({ predicate: (query) => !GLOBAL_QUERIES.has(query.queryKey[0]) });
}

/**
 * Who is online, read over REST when the socket joins: the server pushes the list when it changes, so a person who
 * joins would otherwise see an empty list until someone moves. A list the socket pushes meanwhile wins.
 */
async function seedPresence(): Promise<void> {
  const version = useLiveStore.getState().presenceVersion;
  try {
    const entries = await runQuery("presence", {});
    useLiveStore.getState().seedPresence(entries, version);
  } catch {
    // The channel will tell when something changes.
  }
}

/** Where the live channel's news goes: the stores, the query cache, the toasts. */
export function createLiveSink(queryClient: QueryClient): LiveSink {
  const live = () => useLiveStore.getState();
  return {
    status: (status) => live().setStatus(status),
    hello: (message) => {
      live().setHello(message);
      void seedPresence();
    },
    notice: (notice) => live().pushNotice(notice),
    applied: (head) => live().setHead(head),
    serverHead: (head) => live().setServerHead(head),
    presence: (entries) => live().setPresence(entries),
    lock: (message) => live().setLock(message),
    files: (status) => live().setFiles(status),
    counts: (message) => live().setCounts({ checks: message.checks, counters: message.counters }),
    agent: (message) => {
      const agent = useAgentStore.getState();
      switch (message.type) {
        case "agent.turn_started":
          agent.start(message);
          break;
        case "agent.delta":
          agent.delta(message);
          break;
        case "agent.block":
          agent.block(message);
          break;
        case "agent.tool":
          agent.tool(message);
          break;
        case "agent.turn_ended":
          agent.end(message);
          break;
      }
    },
    personalNotice: (message) => {
      const me = queryClient.getQueryData<MeView>(queryKey("me", {}) as unknown as readonly unknown[]);
      showToast(toastForNotice(message, me));
    },
    pointTo: (message) => {
      if (message.form_draft) useDraftStore.getState().set(message.form_draft);
      pointToBus.emit({
        screen: message.screen,
        element_id: message.element_id,
        spot_id: message.spot_id,
        sentence: message.sentence,
        form_draft: message.form_draft,
      });
      const to = pointToPath(message, { sessionId: getHeartbeatLocation().session_id });
      showToast({ text: message.sentence, action: to ? { label: "Show me", to } : undefined });
    },
    error: (message) => live().setError(`${message.code}: ${message.message}`),
    invalidate: (names) => {
      for (const name of names) void queryClient.invalidateQueries({ queryKey: [name] });
    },
    refetchAll: () => {
      void queryClient.invalidateQueries();
    },
    firstJoin: (since) => {
      // A query read before the socket was open may have missed an event: look at it again.
      void queryClient.invalidateQueries({
        predicate: (query) => !GLOBAL_QUERIES.has(query.queryKey[0]) && query.state.dataUpdatedAt > 0 && query.state.dataUpdatedAt < since,
        refetchType: "active",
      });
    },
    authLost: (why) => {
      if (why === "account_disabled") useAuthNotice.getState().set("Your account has been disabled. Please ask the admin.");
      else useAuthNotice.getState().set("Your session has ended. Please sign in again.");
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
    membershipChanged: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
    checkSession: () => {
      void queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  };
}
